import { useReducer } from 'react';
import { useActionDispatchContextState } from 'lib/ActionDispatchContext';

import {
  ActionType,
  failureActionCreator,
  requestActionCreator,
  successActionCreator,
} from '../actionCreators';
import { useToastError } from './useToastError';
import { EXPRESS_API } from 'configs/api/expressApi';

export interface MoveCopyVideoUpdateBody extends MoveCopyVideoBodyNode {}

interface MoveCopyVideoBodyNode {
  videoId: string;
  copy: boolean;
  videoFolder: string;
}

interface MoveCopyUpdateResponse {
  id: Number;
  success: boolean;
}

export type MoveCopyUpdateMutate = (body: MoveCopyVideoUpdateBody) => void;

interface MoveCopyStoreState {
  data: MoveCopyUpdateResponse | undefined;
  error: string | null;
  loading: boolean;
  isValid: boolean;
}

function reducer(state: MoveCopyStoreState, action: ActionType) {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        data: action.payload,
        loading: false,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        data: undefined,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
}

export const useVideoMoveCopy = () => {
  const { showError } = useToastError();
  const { setAction } = useActionDispatchContextState();

  const initialState = {
    data: undefined,
    loading: false,
    error: null,
    isValid: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const mutate: MoveCopyUpdateMutate = async body => {
    dispatch(
      requestActionCreator({
        type: 'FETCH_REQUEST',
      })
    );

    const { videoId, copy, videoFolder } = body;

    try {
      const { data } = await EXPRESS_API.put(`videos/${videoId}/move`, {
        copy,
        videoFolder,
      });

      dispatch(
        successActionCreator({
          type: 'FETCH_SUCCESS',
          payload: data,
        })
      );

      return {
        data,
        loading: false,
        error: null,
      };
    } catch (err) {
      // Axios automatically throws an error in case of HTTP or Network errors
      // so we can eventually put error parsing here and return whatever value we want based on that.
      showError(err);

      dispatch(
        failureActionCreator({
          type: 'FETCH_FAILURE',
          payload: 'Something went wrong!',
        })
      );

      setAction({ type: 'MOVE_COPY_VIDEO' });

      return Promise.reject({
        loading: false,
        error: 'Something went wrong!',
      });
    }
  };

  const { data, error, loading } = state;

  return {
    mutate,
    data,
    loading,
    error,
  };
};
